"use client"

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { RefreshCw, Database, X, Container, Cpu, Copy, Check, ChevronDown, ChevronUp, Monitor, MapPin } from 'lucide-react';
import type { DeviceStats } from '../../types';

interface DeviceStatsTableProps {
  stats: DeviceStats[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  machines?: any[];
  // When the parent already paginates `stats` itself (e.g. one backend page
  // at a time), pass a size >= the batch length so this table's own internal
  // pagination footer never appears — it only shows when totalPages > 1.
  defaultPageSize?: number;
}

interface AdditionalInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DeviceStats | null;
  onDockerMetricsClick?: (dockerMetrics: any, siteId: string, clientId: string) => void;
  onSystemMetricsClick?: (systemMetrics: any, siteId: string, clientId: string) => void;
  onQureappMetricsClick?: (stats: any, siteId: string, clientId: string) => void;
  onNvidiaMetricsClick?: (nvidiaMetrics: any, siteId: string, clientId: string) => void;
  onLocationClick?: (location: any, siteId: string, clientId: string) => void;
  onVitalsMetricsClick?: (vitalsMetrics: any, siteId: string, clientId: string) => void;
}

// ─── Shared modal UI helpers ────────────────────────────────────────────────

const ModalSectionHeader = ({ title, accent }: { title: string; accent?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="h-4 w-0.5 rounded-full" style={{ backgroundColor: accent || "#00A9A7" }} />
    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
      {title}
    </span>
    <div className="flex-1 h-px" style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }} />
  </div>
);

const statCard = (label: string, value: React.ReactNode, valueColor: string) => (
  <div
    className="p-4 rounded-xl flex flex-col"
    style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.06)", minHeight: "80px" }}
  >
    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
      {label}
    </span>
    <div className="flex-1 flex items-center justify-center">
      <div className="text-2xl font-bold text-center min-w-0 break-all" style={{ color: valueColor }}>
        {value}
      </div>
    </div>
  </div>
);

// ─── Modals ──────────────────────────────────────────────────────────────────

const AdditionalInfoModal: React.FC<AdditionalInfoModalProps> = ({ isOpen, onClose, data, onDockerMetricsClick, onSystemMetricsClick, onQureappMetricsClick, onNvidiaMetricsClick, onLocationClick, onVitalsMetricsClick }) => {
  const [copiedUuid, setCopiedUuid] = useState(false);
  const [showFailedTasks, setShowFailedTasks] = useState(false);
  const [showCathodeFailedTasks, setShowCathodeFailedTasks] = useState(false);

  if (!isOpen || !data) return null;

  // Normalize field name: API returns 'additional' but code expects 'additional_notes'
  const additionalNotes = (data as any).additional || data.additional_notes || {};

  const handleCopyUuid = () => {
    navigator.clipboard.writeText(data.uuid).then(() => {
      setCopiedUuid(true);
      setTimeout(() => setCopiedUuid(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 sticky top-0 z-10 rounded-t-2xl"
          style={{ backgroundColor: "#0f1717", borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div>
            <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
              Event Details
            </div>
            <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
              {data.client_id} / {data.site_id}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-7 py-6 space-y-8">

            {/* ── Record Information ── */}
            <div>
              <ModalSectionHeader title="Record Information" />
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="text-xs font-medium mb-2" style={{ color: "rgba(255, 255, 255, 0.4)" }}>UUID</div>
                  <div className="flex items-center gap-2">
                    <div
                      className="font-mono text-sm p-3 rounded-xl flex-1 truncate"
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.04)", color: "#5eead4", border: "1px solid rgba(255, 255, 255, 0.06)" }}
                    >
                      {data.uuid}
                    </div>
                    <button
                      onClick={handleCopyUuid}
                      className="flex-shrink-0 p-2.5 rounded-xl transition-all duration-200"
                      title="Copy UUID"
                      style={{
                        backgroundColor: copiedUuid ? "rgba(34, 197, 94, 0.15)" : "rgba(255, 255, 255, 0.05)",
                        color: copiedUuid ? "#22c55e" : "rgba(255, 255, 255, 0.4)",
                        border: `1px solid ${copiedUuid ? "rgba(34, 197, 94, 0.3)" : "rgba(255, 255, 255, 0.08)"}`,
                      }}
                    >
                      {copiedUuid ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.06)" }}
                >
                  <div className="text-xs font-medium mb-1" style={{ color: "rgba(255, 255, 255, 0.4)" }}>Batch Index</div>
                  <div className="text-xl font-bold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>{data.batch_index}</div>
                </div>
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.06)" }}
                >
                  <div className="text-xs font-medium mb-1" style={{ color: "rgba(255, 255, 255, 0.4)" }}>Event Received</div>
                  <div className="text-sm font-medium" style={{ color: "rgba(255, 255, 255, 0.85)" }}>
                    {new Date(data.event_received_time).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Task Statistics ── */}
            <div>
              <ModalSectionHeader title="Task Statistics" accent="#6ee7b7" />
              <div className="grid grid-cols-3 gap-3">
                {statCard("Tasks Processed", data.stats.tasks_processed_current || data.stats.tasks_completed || 0, "#22c55e")}
                {statCard("Tasks Pending", data.stats.tasks_pending_current || data.stats.tasks_pending || 0, "#eab308")}
                {statCard("Tasks Received", data.stats.tasks_received_current || 0, "rgba(255,255,255,0.85)")}

                {/* Tasks Failed — expandable */}
                <div
                  className="p-4 rounded-xl flex flex-col cursor-pointer select-none transition-all"
                  style={{
                    backgroundColor: showFailedTasks ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
                    border: showFailedTasks ? "1px solid rgba(239,68,68,0.35)" : "1px solid rgba(255,255,255,0.06)",
                    minHeight: "80px",
                  }}
                  onClick={() => setShowFailedTasks(v => !v)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.4)" }}>
                      Tasks Failed
                    </span>
                    {showFailedTasks
                      ? <ChevronUp className="h-3.5 w-3.5" style={{ color: "#ef4444" }} />
                      : <ChevronDown className="h-3.5 w-3.5" style={{ color: "#ef4444" }} />}
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-2xl font-bold text-center" style={{ color: "#ef4444" }}>
                      {data.stats.tasks_failed_list?.length ?? 0}
                    </div>
                  </div>
                </div>

                {/* Cathode Tasks Failed — expandable */}
                <div
                  className="p-4 rounded-xl flex flex-col cursor-pointer select-none transition-all"
                  style={{
                    backgroundColor: showCathodeFailedTasks ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
                    border: showCathodeFailedTasks ? "1px solid rgba(239,68,68,0.35)" : "1px solid rgba(255,255,255,0.06)",
                    minHeight: "80px",
                  }}
                  onClick={() => setShowCathodeFailedTasks(v => !v)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.4)" }}>
                      Cathode Tasks Failed
                    </span>
                    {showCathodeFailedTasks
                      ? <ChevronUp className="h-3.5 w-3.5" style={{ color: "#ef4444" }} />
                      : <ChevronDown className="h-3.5 w-3.5" style={{ color: "#ef4444" }} />}
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-2xl font-bold text-center" style={{ color: "#ef4444" }}>
                      {(data.stats as any).cathode_tasks_failed_list?.length ?? 0}
                    </div>
                  </div>
                </div>

                {statCard("Images Processed", data.stats.images_processed_current || data.stats.images_processed || 0, "#22c55e")}
                {statCard("Patients Synced", data.stats.patients_synced_current || data.stats.images_synced_current || 0, "#22c55e")}
                {statCard("Patients Sync Pending", data.stats.images_sync_pending_current || data.stats.image_sync_pending || 0, "#eab308")}
                {statCard("Patients Not Synced", data.stats.patients_not_synced || 0, "#ef4444")}
                {statCard(
                  "System Status",
                  <span className="text-lg font-semibold capitalize">{data.stats.system_status || 'Unknown'}</span>,
                  data.stats.system_status === 'healthy' ? "#22c55e" : "#ef4444"
                )}
                {data.stats.processing_time_ms
                  ? statCard("Processing Time", `${data.stats.processing_time_ms}ms`, "rgba(255,255,255,0.85)")
                  : null}
                {data.stats.success_rate
                  ? statCard("Success Rate", `${data.stats.success_rate}%`, "#22c55e")
                  : null}
              </div>

              {/* ── Failed Tasks Detail (expands inline) ── */}
              {showFailedTasks && (
                <div className="mt-4">
                  {data.stats.tasks_failed_list && data.stats.tasks_failed_list.length > 0 ? (
                    <div
                      className="rounded-xl overflow-hidden"
                      style={{ backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(239,68,68,0.2)" }}
                    >
                      <div
                        className="px-4 py-2 text-xs"
                        style={{ backgroundColor: "rgba(239,68,68,0.06)", borderBottom: "1px solid rgba(239,68,68,0.15)", color: "#ef4444" }}
                      >
                        Showing most recent {data.stats.tasks_failed_list.length} failed tasks
                      </div>
                      <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                        <table className="w-full text-sm">
                          <thead className="sticky top-0" style={{ backgroundColor: "rgba(15,23,23,0.97)" }}>
                            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              {["ID", "Type", "Exception Type", "Created At"].map(h => (
                                <th key={h} className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: "#9AA6B2" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data.stats.tasks_failed_list.map((task: any, i: number) => (
                              <tr
                                key={i}
                                style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)")}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                              >
                                <td className="px-4 py-3 font-mono" style={{ color: "#8B98A5" }}>{task.id}</td>
                                <td className="px-4 py-3 max-w-xs truncate" title={task.type} style={{ color: "#cbd5e1" }}>{task.type}</td>
                                <td className="px-4 py-3 font-mono text-xs" style={{ color: task.exception_type ? "#ef4444" : "#64748b" }}>
                                  {task.exception_type ?? "—"}
                                </td>
                                <td className="px-4 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>
                                  {new Date(task.created_at).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="p-4 rounded-xl text-center"
                      style={{ backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
                    >
                      <span className="text-sm" style={{ color: "#64748b" }}>No failed tasks</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Cathode Failed Tasks Detail (expands inline) ── */}
            {showCathodeFailedTasks && (
              <div className="mt-4">
                {(data.stats as any).cathode_tasks_failed_list && (data.stats as any).cathode_tasks_failed_list.length > 0 ? (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(239,68,68,0.2)" }}
                  >
                    <div
                      className="px-4 py-2 text-xs"
                      style={{ backgroundColor: "rgba(239,68,68,0.06)", borderBottom: "1px solid rgba(239,68,68,0.15)", color: "#ef4444" }}
                    >
                      Showing most recent {(data.stats as any).cathode_tasks_failed_list.length} cathode failed tasks
                    </div>
                    <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                      <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ backgroundColor: "rgba(15,23,23,0.97)" }}>
                          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                            {["ID", "Type", "Exception Type", "Created At"].map(h => (
                              <th key={h} className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: "#9AA6B2" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(data.stats as any).cathode_tasks_failed_list.map((task: any, i: number) => (
                            <tr
                              key={i}
                              style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)")}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                            >
                              <td className="px-4 py-3 font-mono" style={{ color: "#8B98A5" }}>{task.id}</td>
                              <td className="px-4 py-3 max-w-xs truncate" title={task.type} style={{ color: "#cbd5e1" }}>{task.type}</td>
                              <td className="px-4 py-3 font-mono text-xs" style={{ color: task.exception_type ? "#ef4444" : "#64748b" }}>
                                {task.exception_type ?? "—"}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>
                                {new Date(task.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
                  >
                    <span className="text-sm" style={{ color: "#64748b" }}>No cathode failed tasks</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Qureapp Metrics ── */}
            <div>
              <ModalSectionHeader title="Qureapp Metrics" accent="#6366f1" />
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onQureappMetricsClick?.(data.stats, data.site_id, data.client_id)}
                  className="border-0 h-9 px-4 text-sm cursor-pointer"
                  style={{ background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", color: "#ffffff" }}
                >
                  <Database className="h-4 w-4 mr-2" />
                  View Qureapp Metrics
                </Button>
              </div>
            </div>

            {/* ── System Metrics ── */}
            {data.stats.system_metrics && typeof data.stats.system_metrics === 'object' && (
              <div>
                <ModalSectionHeader title="System Metrics" accent="#10b981" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSystemMetricsClick?.(data.stats.system_metrics, data.site_id, data.client_id)}
                    className="border-0 h-9 px-4 text-sm cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#ffffff" }}
                  >
                    <Cpu className="h-4 w-4 mr-2" />
                    View System Metrics
                  </Button>
                </div>
              </div>
            )}

            {/* ── Docker Metrics ── */}
            {data.stats.docker_metrics && typeof data.stats.docker_metrics === 'object' && (
              <div>
                <ModalSectionHeader title="Docker Metrics" accent="#06b6d4" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDockerMetricsClick?.(data.stats.docker_metrics, data.site_id, data.client_id)}
                    className="border-0 h-9 px-4 text-sm cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)", color: "#ffffff" }}
                  >
                    <Container className="h-4 w-4 mr-2" />
                    View Docker Metrics
                  </Button>
                </div>
              </div>
            )}

            {/* ── Vitals Metrics ── */}
            {data.vitals_metrics && typeof data.vitals_metrics === 'object' && (
              <div>
                <ModalSectionHeader title="Vitals Metrics" accent="#8b5cf6" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onVitalsMetricsClick?.(data.vitals_metrics, data.site_id, data.client_id)}
                    className="border-0 h-9 px-4 text-sm cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)", color: "#ffffff" }}
                  >
                    <Monitor className="h-4 w-4 mr-2" />
                    View Vitals Metrics
                  </Button>
                </div>
              </div>
            )}

            {/* ── NVIDIA GPU Metrics ── */}
            {data.stats.nvidia_metrics && typeof data.stats.nvidia_metrics === 'object' && (
              <div>
                <ModalSectionHeader title="NVIDIA GPU Metrics" accent="#76b900" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onNvidiaMetricsClick?.(data.stats.nvidia_metrics, data.site_id, data.client_id)}
                    className="border-0 h-9 px-4 text-sm cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #76b900 0%, #5a8c00 100%)", color: "#ffffff" }}
                  >
                    <Monitor className="h-4 w-4 mr-2" />
                    View NVIDIA Metrics
                  </Button>
                </div>
              </div>
            )}

            {/* ── Location ── */}
            {data.stats.location && typeof data.stats.location === 'object' && (
              <div>
                <ModalSectionHeader title="Location" accent="#f97316" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onLocationClick?.(data.stats.location, data.site_id, data.client_id)}
                    className="border-0 h-9 px-4 text-sm cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", color: "#ffffff" }}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    View Location
                  </Button>
                </div>
              </div>
            )}

            {/* ── Processing Notes ── */}
            {additionalNotes.processing_notes && (
              <div>
                <ModalSectionHeader title="Processing Notes" accent="#fbbf24" />
                <div
                  className="p-4 rounded-xl text-sm leading-relaxed"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  {additionalNotes.processing_notes}
                </div>
              </div>
            )}



            {/* ── Error Details ── */}
            {additionalNotes.error_details && (
              <div>
                <ModalSectionHeader title="Error Details" accent="#ef4444" />
                <div
                  className="p-4 rounded-xl text-sm border-l-2 leading-relaxed"
                  style={{ backgroundColor: "rgba(239, 68, 68, 0.05)", color: "#fca5a5", borderLeftColor: "#ef4444" }}
                >
                  {additionalNotes.error_details}
                </div>
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
};

interface DockerMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dockerMetrics: any;
  siteId: string;
  clientId: string;
}

const DockerMetricsModal: React.FC<DockerMetricsModalProps> = ({ isOpen, onClose, dockerMetrics, siteId, clientId }) => {
  if (!isOpen || !dockerMetrics) return null;

  const containers = dockerMetrics.containers_details || dockerMetrics.containers || [];

  const running = dockerMetrics.containers_running ?? containers.filter((c: any) => c.state === 'running').length;
  const exited = dockerMetrics.containers_exited ?? containers.filter((c: any) => c.state === 'exited').length;
  const paused = dockerMetrics.containers_paused ?? 0;
  const restarting = containers.filter((c: any) => c.state === 'restarting').length;
  const total = dockerMetrics.containers_total ?? containers.length;

  const containerStateStyle = (state: string): { color: string; bg: string } => {
    switch (state?.toLowerCase()) {
      case 'running':    return { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' };
      case 'exited':     return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
      case 'restarting': return { color: '#eab308', bg: 'rgba(234, 179, 8, 0.1)' };
      case 'paused':     return { color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.1)' };
      default:           return { color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 rounded-t-2xl"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: "rgba(6, 182, 212, 0.15)" }}>
              <Container className="h-5 w-5" style={{ color: "#06b6d4" }} />
            </div>
            <div>
              <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                Docker Metrics
              </div>
              <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                {clientId} / {siteId}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-7 py-6 space-y-8">

            {/* Container Summary */}
            <div>
              <ModalSectionHeader title="Container Summary" accent="#06b6d4" />
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {statCard("Running", running, "#22c55e")}
                {statCard("Exited", exited, "#ef4444")}
                {statCard("Restarting", restarting, "#eab308")}
                {statCard("Paused", paused, "#a78bfa")}
                {statCard("Total", total, "rgba(255, 255, 255, 0.85)")}
              </div>
            </div>

            {/* Container Table */}
            {containers.length > 0 && (
              <div>
                <ModalSectionHeader title="Containers" accent="#06b6d4" />
                <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255, 255, 255, 0.06)" }}>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                        {['Name', 'Image', 'State', 'Uptime'].map(col => (
                          <th key={col} className={`${col === 'Uptime' ? 'text-right' : 'text-left'} px-4 py-3 text-xs font-semibold uppercase tracking-wider`} style={{ color: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {containers.map((container: any, index: number) => {
                        const stateStyle = containerStateStyle(container.state);
                        const imageName = container.image ? container.image.split('/').pop()?.split(':')[0] || container.image : 'N/A';
                        const imageTag = container.image ? container.image.split(':').pop() : '';
                        return (
                          <tr
                            key={container.name || container.id || index}
                            style={{
                              backgroundColor: index % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                              borderBottom: "1px solid rgba(255,255,255,0.04)",
                            }}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-left" style={{ color: "rgba(255,255,255,0.85)" }}>
                              {container.name || 'Unknown'}
                            </td>
                            <td className="px-4 py-3 text-sm text-left" style={{ color: "rgba(255,255,255,0.5)" }}>
                              <span title={container.image}>{imageName}</span>
                              {imageTag && <span className="ml-1 text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>:{imageTag.substring(0, 12)}</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-left">
                              <span
                                className="px-2.5 py-1 rounded-full text-xs font-semibold"
                                style={{ color: stateStyle.color, backgroundColor: stateStyle.bg, border: `1px solid ${stateStyle.color}40` }}
                              >
                                {container.state || 'Unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>
                              {container.uptime_human || container.status || 'N/A'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

interface VitalsMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vitalsMetrics: any;
  siteId: string;
  clientId: string;
}

const VitalsMetricsModal: React.FC<VitalsMetricsModalProps> = ({ isOpen, onClose, vitalsMetrics, siteId, clientId }) => {
  if (!isOpen || !vitalsMetrics) return null;

  const summary = vitalsMetrics.summary || {};
  const repairs = vitalsMetrics.repairs_by_action || {};
  const period = vitalsMetrics.period || {};
  const notifications = vitalsMetrics.notifications || {};
  const notificationsByType = notifications.by_type || {};
  const recoveryRate = summary.automatic_recovery_rate_percent != null
    ? `${Number(summary.automatic_recovery_rate_percent).toFixed(1)}%`
    : 'N/A';

  const statCard = (label: string, value: any, color: string) => (
    <div key={label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px 16px', minWidth: 100, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value ?? 'N/A'}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{label}</div>
    </div>
  );

  const sectionHeader = (title: string) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{title}</div>
  );

  const incidentStats: Array<[string, any, string]> = [
    ['Incidents Started', summary.incidents_started, '#f59e0b'],
    ['Affected Components', summary.affected_components, '#f59e0b'],
    ['Required Incidents', summary.required_incidents, '#f59e0b'],
    ['Optional Incidents', summary.optional_incidents, '#94a3b8'],
    ['Closed Required', summary.closed_required_incidents, '#22c55e'],
    ['Ongoing', summary.ongoing_incidents, summary.ongoing_incidents > 0 ? '#ef4444' : '#22c55e'],
    ['Escalated', summary.escalated_incidents, summary.escalated_incidents > 0 ? '#ef4444' : '#22c55e'],
    ['Monitoring Interrupted', summary.monitoring_interrupted_incidents, summary.monitoring_interrupted_incidents > 0 ? '#ef4444' : '#22c55e'],
  ];

  const recoveryStats: Array<[string, any, string]> = [
    ['Auto Recovery Rate', recoveryRate, '#22c55e'],
    ['Auto-Recovered Components', summary.automatically_recovered_components, '#22c55e'],
    ['Recovered After Failed Repair', summary.recovered_after_failed_repair, '#60a5fa'],
    ['Recovered After Escalation', summary.recovered_after_escalation, '#60a5fa'],
    ['Recovered Without Repair', summary.recovered_without_repair, '#60a5fa'],
    ['Manual Restart Outcome Unknown', summary.manual_restart_outcome_unknown, '#94a3b8'],
  ];

  const repairStats: Array<[string, any, string]> = [
    ['Repair Attempts', summary.repair_attempts, '#60a5fa'],
    ['Successful Repair Commands', summary.successful_repair_commands, '#22c55e'],
    ['Failed Repair Commands', summary.failed_repair_commands, summary.failed_repair_commands > 0 ? '#ef4444' : '#22c55e'],
  ];

  const platformStats: Array<[string, any, string]> = [
    ['Platform Restarts Started', summary.platform_restarts_started, '#94a3b8'],
    ['Platform Restarts Completed', summary.platform_restarts_completed, '#94a3b8'],
    ['Platform Restart Failures', summary.platform_restart_project_failures, summary.platform_restart_project_failures > 0 ? '#ef4444' : '#22c55e'],
    ['System Reboots Requested', summary.system_reboots_requested, '#94a3b8'],
    ['Notifications Spawned', summary.notifications_spawned, '#94a3b8'],
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#0f172a', borderRadius: 16, border: '1px solid rgba(139,92,246,0.3)', width: '90%', maxWidth: 760, maxHeight: '85vh', overflowY: 'auto', padding: 28, position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
          <X className="h-5 w-5" />
        </button>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Monitor style={{ color: '#8b5cf6' }} className="h-5 w-5" />
            <span style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>Vitals Metrics</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{siteId} / {clientId}</div>
          {period.from && period.to && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              Period: {new Date(period.from).toLocaleDateString()} → {new Date(period.to).toLocaleDateString()} ({period.days}d)
            </div>
          )}
          {vitalsMetrics.generated_at && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              Generated: {new Date(vitalsMetrics.generated_at).toLocaleString()}
            </div>
          )}
          {vitalsMetrics.configured_app_version && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              App Version: {vitalsMetrics.configured_app_version}
            </div>
          )}
        </div>

        {/* Incidents */}
        <div style={{ marginBottom: 20 }}>
          {sectionHeader('Incident Summary')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {incidentStats.map(([label, value, color]) => statCard(label, value, color))}
          </div>
        </div>

        {/* Recovery */}
        <div style={{ marginBottom: 20 }}>
          {sectionHeader('Recovery Breakdown')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {recoveryStats.map(([label, value, color]) => statCard(label, value, color))}
          </div>
        </div>

        {/* Repairs */}
        <div style={{ marginBottom: 20 }}>
          {sectionHeader('Repair Commands')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {repairStats.map(([label, value, color]) => statCard(label, value, color))}
          </div>
        </div>

        {/* Platform / reboots / notifications count */}
        <div style={{ marginBottom: 20 }}>
          {sectionHeader('Platform & Notifications')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {platformStats.map(([label, value, color]) => statCard(label, value, color))}
          </div>
        </div>

        {/* Repairs by action */}
        {Object.keys(repairs).length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {sectionHeader('Repairs by Action')}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#64748b', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    {['Action', 'Attempts', 'Succeeded', 'Failed', 'Recovered'].map(h => (
                      <th key={h} style={{ padding: '6px 12px', textAlign: h === 'Action' ? 'left' : 'center', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(repairs).map(([action, r]: [string, any]) => (
                    <tr key={action} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#cbd5e1' }}>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{action}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{r.attempts ?? 0}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: '#22c55e' }}>{r.command_successes ?? 0}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: r.command_failures > 0 ? '#ef4444' : '#94a3b8' }}>{r.command_failures ?? 0}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: '#60a5fa' }}>{r.confirmed_recoveries ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notifications by type */}
        {Object.keys(notificationsByType).length > 0 && (
          <div>
            {sectionHeader(`Notifications by Type (Total Spawned: ${notifications.total_spawned ?? 0})`)}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#64748b', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    {['Type', 'Spawned', 'CTA Clicks'].map(h => (
                      <th key={h} style={{ padding: '6px 12px', textAlign: h === 'Type' ? 'left' : 'center', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(notificationsByType).map(([type, n]: [string, any]) => {
                    const ctaEntries = Object.entries(n?.cta || {}) as [string, any][];
                    const ctaText = ctaEntries.length > 0
                      ? ctaEntries.map(([ctaName, count]) => `${ctaName}: ${count}`).join(', ')
                      : '-';
                    return (
                      <tr key={type} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#cbd5e1' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{type}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>{n?.spawned ?? 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>{ctaText}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface SystemMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  systemMetrics: any;
  siteId: string;
  clientId: string;
}

const SystemMetricsModal: React.FC<SystemMetricsModalProps> = ({ isOpen, onClose, systemMetrics, siteId, clientId }) => {
  if (!isOpen || !systemMetrics) return null;

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const getUsageColor = (percent: number): string => {
    if (percent < 50) return '#22c55e';
    if (percent < 80) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 rounded-t-2xl"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div>
            <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
              System Metrics
            </div>
            <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
              {clientId} / {siteId}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-7 py-6 space-y-8">

            {/* System Information */}
            {systemMetrics.system && (
              <div>
                <ModalSectionHeader title="System Information" />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {statCard("Hostname", <span className="text-lg font-semibold">{systemMetrics.system.hostname || 'N/A'}</span>, "rgba(255,255,255,0.85)")}
                  {statCard("OS Version", <span className="font-mono text-sm">{systemMetrics.system.os_version || 'N/A'}</span>, "rgba(255,255,255,0.85)")}
                  {statCard("Kernel Version", <span className="font-mono text-sm">{systemMetrics.system.kernel_version || 'N/A'}</span>, "rgba(255,255,255,0.85)")}
                  {statCard("CPU Count", systemMetrics.system.cpu_count || 'N/A', "rgba(255,255,255,0.85)")}
                  {statCard("Uptime", systemMetrics.system.uptime_seconds ? formatUptime(systemMetrics.system.uptime_seconds) : 'N/A', "#22c55e")}
                  {statCard("Load Avg (1m)", systemMetrics.system.load_average_1min || 'N/A', "rgba(255,255,255,0.85)")}
                  {statCard("Last Reboot", <span className="text-sm">{systemMetrics.system.last_reboot ? new Date(systemMetrics.system.last_reboot).toLocaleString() : 'N/A'}</span>, "rgba(255,255,255,0.7)")}
                </div>
              </div>
            )}

            {/* Memory Usage */}
            {systemMetrics.memory && (
              <div>
                <ModalSectionHeader title="Memory Usage" accent="#6ee7b7" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {statCard("Total", systemMetrics.memory.total_gb || formatBytes(systemMetrics.memory.total_bytes), "rgba(255,255,255,0.85)")}
                  {statCard("Used", systemMetrics.memory.used_gb || formatBytes(systemMetrics.memory.used_bytes), getUsageColor(systemMetrics.memory.usage_percent || 0))}
                  {statCard("Available", systemMetrics.memory.available_gb || formatBytes(systemMetrics.memory.available_bytes), "#22c55e")}
                  {statCard("Usage", systemMetrics.memory.usage_percent != null ? `${systemMetrics.memory.usage_percent}%` : 'N/A', getUsageColor(systemMetrics.memory.usage_percent || 0))}
                </div>
              </div>
            )}

            {/* Disk Usage */}
            {systemMetrics.disks && systemMetrics.disks.length > 0 && (
              <div>
                <ModalSectionHeader title="Disk Usage" accent="#6366f1" />
                <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255, 255, 255, 0.06)" }}>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                        {['Device', 'Mount Point', 'Filesystem', 'Total', 'Used', 'Available', 'Usage %'].map(col => (
                          <th key={col} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {systemMetrics.disks.map((disk: any, index: number) => (
                        <tr
                          key={disk.device || index}
                          style={{
                            backgroundColor: index % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                          }}
                        >
                          <td className="px-4 py-3 text-sm font-medium font-mono" style={{ color: "rgba(255,255,255,0.85)" }}>{disk.device || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm font-mono" style={{ color: "rgba(255,255,255,0.7)" }}>{disk.mountpoint || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>{disk.filesystem || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{disk.total_gb || formatBytes(disk.total_bytes) || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{disk.used_gb || formatBytes(disk.used_bytes) || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{disk.available_gb || formatBytes(disk.available_bytes) || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className="px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{
                                color: getUsageColor(disk.usage_percent || 0),
                                backgroundColor: `${getUsageColor(disk.usage_percent || 0)}1a`,
                                border: `1px solid ${getUsageColor(disk.usage_percent || 0)}40`,
                              }}
                            >
                              {disk.usage_percent != null ? `${disk.usage_percent}%` : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Process Information */}
            {systemMetrics.processes && (
              <div>
                <ModalSectionHeader title="Process Information" accent="#f59e0b" />
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {statCard("Total", systemMetrics.processes.total_processes || 'N/A', "rgba(255,255,255,0.85)")}
                  {statCard("Running", systemMetrics.processes.running_processes || 'N/A', "#22c55e")}
                  {statCard("Sleeping", systemMetrics.processes.sleeping_processes || 'N/A', "rgba(255,255,255,0.5)")}
                  {statCard("Zombie", systemMetrics.processes.zombie_processes || 0, systemMetrics.processes.zombie_processes > 0 ? "#ef4444" : "#22c55e")}
                  {statCard("Stopped", systemMetrics.processes.stopped_processes || 0, "rgba(255,255,255,0.5)")}
                </div>
              </div>
            )}

            {/* Service Information */}
            {systemMetrics.services && (
              <div>
                <ModalSectionHeader title="Service Information" accent="#94a3b8" />
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {statCard("Total", systemMetrics.services.total_services || 'N/A', "rgba(255,255,255,0.85)")}
                  {statCard("Running", systemMetrics.services.running_services || 'N/A', "#22c55e")}
                  {statCard("Failed", systemMetrics.services.failed_services || 0, systemMetrics.services.failed_services > 0 ? "#ef4444" : "#22c55e")}
                </div>
                {systemMetrics.services.failed_service_names && systemMetrics.services.failed_service_names.length > 0 && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                    <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#ef4444" }}>Failed Services</div>
                    <div className="flex flex-wrap gap-2">
                      {systemMetrics.services.failed_service_names.map((service: string, index: number) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.3)" }}
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Qureapp Metrics Modal ────────────────────────────────────────────────

interface QureappMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: any;
  siteId: string;
  clientId: string;
}

const metricGroups: { title: string; accent: string; match: (key: string) => boolean }[] = [
  {
    title: 'Sync Status',
    accent: '#22c55e',
    match: (k) => /(synced|sync_pending|patients_not_synced|patients_synced|patients_total)/.test(k),
  },
  {
    title: 'Tasks',
    accent: '#eab308',
    match: (k) => /^tasks_/.test(k),
  },
  {
    title: 'Images',
    accent: '#06b6d4',
    match: (k) => /^images_/.test(k),
  },
  {
    title: 'Cathode',
    accent: '#f59e0b',
    match: (k) => /^cathode_/.test(k),
  },
  {
    title: 'Other',
    accent: '#94a3b8',
    match: () => true,
  },
];

const MetricGroupTable: React.FC<{
  title: string;
  accent: string;
  entries: [string, any][];
  defaultOpen?: boolean;
}> = ({ title, accent, entries, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const formatStatKey = (key: string) => {
    if (key === 'images_synced_current') return 'Patient Synced Current';
    if (key === 'images_sync_pending_current') return 'Patient Sync Pending Current';
    if (key === 'images_processed_current') return 'Image Processed Current';
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (entries.length === 0) return null;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255, 255, 255, 0.06)" }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left transition-colors"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
      >
        <div className="flex items-center gap-3">
          <div className="h-4 w-1 rounded-full" style={{ backgroundColor: accent }} />
          <span className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
            {title}
          </span>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: `${accent}1a`, color: accent, border: `1px solid ${accent}40` }}
          >
            {entries.length}
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4" style={{ color: "rgba(255,255,255,0.4)" }} />
          : <ChevronDown className="h-4 w-4" style={{ color: "rgba(255,255,255,0.4)" }} />
        }
      </button>
      {open && (
        <table className="w-full border-collapse">
          <tbody>
            {entries.map(([key, value], index) => (
              <tr
                key={key}
                style={{
                  backgroundColor: index % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <td className="px-5 py-3 text-sm font-medium text-left" style={{ color: "rgba(255,255,255,0.5)" }}>
                  {formatStatKey(key)}
                </td>
                <td className="px-5 py-3 text-sm text-right" style={{ color: "rgba(255,255,255,0.85)" }}>
                  {value !== null && value !== undefined
                    ? (Array.isArray(value) ? value.length : String(value))
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const QureappMetricsModal: React.FC<QureappMetricsModalProps> = ({ isOpen, onClose, stats: qStats, siteId, clientId }) => {
  const [showFailedTasks, setShowFailedTasks] = useState(false);
  const [showCathodeFailedTasks, setShowCathodeFailedTasks] = useState(false);

  if (!isOpen || !qStats) return null;

  const failedTasksList: any[] = Array.isArray(qStats.tasks_failed_list) ? qStats.tasks_failed_list : [];
  const cathodeFailedTasksList: any[] = Array.isArray(qStats.cathode_tasks_failed_list) ? qStats.cathode_tasks_failed_list : [];

  const excludeKeys = ['docker_metrics', 'system_metrics', 'compute', 'nvidia_metrics', 'tasks_failed_list', 'cathode_tasks_failed_list'];
  const allEntries = Object.entries(qStats).filter(
    ([key]) => !excludeKeys.includes(key) && (qStats[key] == null || typeof qStats[key] !== 'object' || Array.isArray(qStats[key]))
  );

  const grouped: { title: string; accent: string; entries: [string, any][] }[] = [];
  const claimed = new Set<string>();

  for (const group of metricGroups) {
    const matching = allEntries.filter(([key]) => !claimed.has(key) && group.match(key));
    matching.forEach(([key]) => claimed.add(key));
    if (matching.length > 0 || group.title !== 'Other') {
      grouped.push({ title: group.title, accent: group.accent, entries: matching });
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        <div
          className="flex items-center justify-between px-7 py-5 rounded-t-2xl"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div>
            <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
              Qureapp Metrics
            </div>
            <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
              {clientId} / {siteId}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-7 py-6 space-y-4">
            {grouped.map((g, i) => (
              <MetricGroupTable
                key={g.title}
                title={g.title}
                accent={g.accent}
                entries={g.entries}
                defaultOpen={i === 0}
              />
            ))}

            {/* ── Tasks Failed List ── */}
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(239,68,68,0.25)" }}>
              <button
                onClick={() => setShowFailedTasks(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left transition-colors"
                style={{ backgroundColor: showFailedTasks ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-4 w-1 rounded-full" style={{ backgroundColor: "#ef4444" }} />
                  <span className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Tasks Failed
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}
                  >
                    {failedTasksList.length}
                  </span>
                </div>
                {showFailedTasks
                  ? <ChevronUp className="h-4 w-4" style={{ color: "#ef4444" }} />
                  : <ChevronDown className="h-4 w-4" style={{ color: "#ef4444" }} />}
              </button>
              {showFailedTasks && (
                failedTasksList.length > 0 ? (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className="w-full border-collapse text-sm">
                      <thead className="sticky top-0" style={{ backgroundColor: "rgba(15,23,23,0.97)" }}>
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                          {["ID", "Type", "Exception Type", "Created At"].map(h => (
                            <th key={h} className="px-5 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: "#9AA6B2" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {failedTasksList.map((task: any, i: number) => (
                          <tr
                            key={i}
                            style={{ backgroundColor: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.05)")}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)")}
                          >
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>{task.id}</td>
                            <td className="px-5 py-3 max-w-xs truncate" title={task.type} style={{ color: "#cbd5e1" }}>{task.type}</td>
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: task.exception_type ? "#ef4444" : "#64748b" }}>
                              {task.exception_type ?? "—"}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>
                              {new Date(task.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-4 text-sm" style={{ color: "#64748b" }}>No failed tasks</div>
                )
              )}
            </div>

            {/* ── Cathode Tasks Failed List ── */}
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(239,68,68,0.25)" }}>
              <button
                onClick={() => setShowCathodeFailedTasks(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left transition-colors"
                style={{ backgroundColor: showCathodeFailedTasks ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-4 w-1 rounded-full" style={{ backgroundColor: "#ef4444" }} />
                  <span className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Cathode Tasks Failed
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}
                  >
                    {cathodeFailedTasksList.length}
                  </span>
                </div>
                {showCathodeFailedTasks
                  ? <ChevronUp className="h-4 w-4" style={{ color: "#ef4444" }} />
                  : <ChevronDown className="h-4 w-4" style={{ color: "#ef4444" }} />}
              </button>
              {showCathodeFailedTasks && (
                cathodeFailedTasksList.length > 0 ? (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className="w-full border-collapse text-sm">
                      <thead className="sticky top-0" style={{ backgroundColor: "rgba(15,23,23,0.97)" }}>
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                          {["ID", "Type", "Exception Type", "Created At"].map(h => (
                            <th key={h} className="px-5 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: "#9AA6B2" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cathodeFailedTasksList.map((task: any, i: number) => (
                          <tr
                            key={i}
                            style={{ backgroundColor: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.05)")}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)")}
                          >
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>{task.id}</td>
                            <td className="px-5 py-3 max-w-xs truncate" title={task.type} style={{ color: "#cbd5e1" }}>{task.type}</td>
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: task.exception_type ? "#ef4444" : "#64748b" }}>
                              {task.exception_type ?? "—"}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs" style={{ color: "#8B98A5" }}>
                              {new Date(task.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-4 text-sm" style={{ color: "#64748b" }}>No cathode failed tasks</div>
                )
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

// ─── NVIDIA GPU Metrics Modal ─────────────────────────────────────────────

interface NvidiaMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  nvidiaMetrics: any;
  siteId: string;
  clientId: string;
}

const NvidiaMetricsModal: React.FC<NvidiaMetricsModalProps> = ({ isOpen, onClose, nvidiaMetrics, siteId, clientId }) => {
  if (!isOpen || !nvidiaMetrics) return null;

  const getTempColor = (temp: number): string => {
    if (temp >= 80) return '#ef4444';
    if (temp >= 60) return '#eab308';
    return '#22c55e';
  };

  const memUsed = nvidiaMetrics.memory_used_mib ?? 0;
  const memTotal = nvidiaMetrics.memory_total_mib ?? 1;
  const memPercent = Math.round((memUsed / memTotal) * 100);
  const pwrUsed = nvidiaMetrics.power_usage_watts ?? 0;
  const pwrCap = nvidiaMetrics.power_cap_watts ?? 1;
  const pwrPercent = Math.round((pwrUsed / pwrCap) * 100);

  const activeColor = nvidiaMetrics.active ? '#22c55e' : '#ef4444';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 rounded-t-2xl"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: "rgba(118, 185, 0, 0.15)" }}>
              <Monitor className="h-5 w-5" style={{ color: "#76b900" }} />
            </div>
            <div>
              <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                NVIDIA GPU Metrics
              </div>
              <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                {clientId} / {siteId}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-7 py-6 space-y-6">

            {/* Status + Driver row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {statCard("Status",
                <span className="text-lg font-bold" style={{ color: activeColor }}>{nvidiaMetrics.active ? 'Active' : 'Inactive'}</span>,
                activeColor
              )}
              {statCard("NVIDIA-SMI", nvidiaMetrics.nvidia_smi_version || 'N/A', "#76b900")}
              {statCard("Driver", nvidiaMetrics.driver_version || 'N/A', "rgba(255,255,255,0.85)")}
              {statCard("CUDA", nvidiaMetrics.cuda_version || 'N/A', "#06b6d4")}
            </div>

            {/* GPU model + temp + power */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {statCard("GPU Model", nvidiaMetrics.gpu_model || 'N/A', "rgba(255,255,255,0.85)")}
              {statCard("Temperature",
                <span style={{ color: getTempColor(nvidiaMetrics.temperature_celsius ?? 0) }}>
                  {nvidiaMetrics.temperature_celsius != null ? `${nvidiaMetrics.temperature_celsius}°C` : 'N/A'}
                </span>,
                getTempColor(nvidiaMetrics.temperature_celsius ?? 0)
              )}
              {statCard("Power",
                `${pwrUsed}W / ${pwrCap}W`,
                pwrPercent >= 80 ? '#ef4444' : '#eab308'
              )}
            </div>

            {/* VRAM bar */}
            {nvidiaMetrics.memory_used_mib != null && nvidiaMetrics.memory_total_mib != null && (
              <div className="p-4 rounded-xl space-y-2" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>VRAM Usage</span>
                  <span className="text-lg font-bold" style={{ color: '#76b900' }}>{memPercent}%</span>
                </div>
                <div className="w-full rounded-full h-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(memPercent, 100)}%`, backgroundColor: '#76b900' }} />
                </div>
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {memUsed.toLocaleString()} MiB / {memTotal.toLocaleString()} MiB
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

interface GroupedStats {
  id: string;
  client_id: string;
  site_id: string;
  start_time: string;
  end_time: string;
  total_images_processed: number;
  total_images_synced: number;
  total_images_pending: number;
  patients_synced: number;
  patients_not_synced: number;
  patients_total: number;
  record_count: number;
  latest_event: string;
  sync_status: {
    color: string;
    bgColor: string;
    text: string;
  };
  raw_records: DeviceStats[];
  additional_data: {
    priorities: string[];
    tags: string[];
    processing_nodes: string[];
    environments: string[];
    error_count: number;
    total_batches: number;
  };
}

// ─── Location Modal ───────────────────────────────────────────────────────────

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: any;
  siteId: string;
  clientId: string;
}

const LocationModal: React.FC<LocationModalProps> = ({ isOpen, onClose, location, siteId, clientId }) => {
  if (!isOpen || !location) return null;

  const { latitude, longitude, city, country, ip } = location;
  const mapsUrl = latitude && longitude
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-lg w-full"
        style={{ backgroundColor: "#0f1717", border: "1px solid rgba(255, 255, 255, 0.08)", boxShadow: "0 24px 64px rgba(0, 0, 0, 0.7)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 rounded-t-2xl"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: "rgba(249, 115, 22, 0.15)" }}>
              <MapPin className="h-5 w-5" style={{ color: "#f97316" }} />
            </div>
            <div>
              <div className="text-base font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                Location
              </div>
              <div className="text-sm mt-0.5" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                {clientId} / {siteId}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.06)", color: "rgba(255, 255, 255, 0.5)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-7 py-6 space-y-6">
          {/* City + Country */}
          <div
            className="flex items-center gap-4 p-4 rounded-xl"
            style={{ backgroundColor: "rgba(249, 115, 22, 0.06)", border: "1px solid rgba(249, 115, 22, 0.2)" }}
          >
            <MapPin className="h-8 w-8 flex-shrink-0" style={{ color: "#f97316" }} />
            <div>
              <div className="text-xl font-bold" style={{ color: "rgba(255,255,255,0.9)" }}>
                {city || 'Unknown City'}
              </div>
              <div className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                {country || 'Unknown Country'}
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div>
            <ModalSectionHeader title="Coordinates & Network" accent="#f97316" />
            <div className="grid grid-cols-3 gap-3">
              {statCard("Latitude", <span className="text-base font-bold">{latitude ?? 'N/A'}</span>, "#f97316")}
              {statCard("Longitude", <span className="text-base font-bold">{longitude ?? 'N/A'}</span>, "#f97316")}
              {statCard("Public IP", <span className="text-sm font-mono break-all">{ip || 'N/A'}</span>, "rgba(255,255,255,0.75)")}
            </div>
          </div>

          {/* Google Maps link */}
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-10 rounded-xl text-sm font-medium transition-opacity hover:opacity-80"
              style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", color: "#ffffff" }}
            >
              <MapPin className="h-4 w-4" />
              Open in Google Maps
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export const DeviceStatsTable: React.FC<DeviceStatsTableProps> = ({
  stats,
  loading,
  error,
  onRefresh,
  machines = [],
  defaultPageSize = 20,
}) => {
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [modalData, setModalData] = useState<DeviceStats | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultPageSize);

  const handlePageSizeChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  // Reset to page 1 whenever the incoming stats change (filter applied from parent)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [stats]);

  // Docker metrics modal state
  const [isDockerMetricsModalOpen, setIsDockerMetricsModalOpen] = useState(false);
  const [dockerMetricsData, setDockerMetricsData] = useState<any>(null);
  const [dockerMetricsSiteId, setDockerMetricsSiteId] = useState<string>('');
  const [dockerMetricsClientId, setDockerMetricsClientId] = useState<string>('');

  // Vitals metrics modal state
  const [isVitalsMetricsModalOpen, setIsVitalsMetricsModalOpen] = useState(false);
  const [vitalsMetricsData, setVitalsMetricsData] = useState<any>(null);
  const [vitalsMetricsSiteId, setVitalsMetricsSiteId] = useState<string>('');
  const [vitalsMetricsClientId, setVitalsMetricsClientId] = useState<string>('');

  // System metrics modal state
  const [isSystemMetricsModalOpen, setIsSystemMetricsModalOpen] = useState(false);
  const [systemMetricsData, setSystemMetricsData] = useState<any>(null);
  const [systemMetricsSiteId, setSystemMetricsSiteId] = useState<string>('');
  const [systemMetricsClientId, setSystemMetricsClientId] = useState<string>('');

  // Qureapp metrics modal state
  const [isQureappMetricsModalOpen, setIsQureappMetricsModalOpen] = useState(false);
  const [qureappMetricsData, setQureappMetricsData] = useState<any>(null);
  const [qureappMetricsSiteId, setQureappMetricsSiteId] = useState<string>('');
  const [qureappMetricsClientId, setQureappMetricsClientId] = useState<string>('');

  // NVIDIA metrics modal state
  const [isNvidiaMetricsModalOpen, setIsNvidiaMetricsModalOpen] = useState(false);
  const [nvidiaMetricsData, setNvidiaMetricsData] = useState<any>(null);
  const [nvidiaMetricsSiteId, setNvidiaMetricsSiteId] = useState<string>('');
  const [nvidiaMetricsClientId, setNvidiaMetricsClientId] = useState<string>('');

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationData, setLocationData] = useState<any>(null);
  const [locationSiteId, setLocationSiteId] = useState<string>('');
  const [locationClientId, setLocationClientId] = useState<string>('');

  // Group stats by unique client-site combinations
  const groupedStats = React.useMemo(() => {
    const groups: { [key: string]: GroupedStats } = {};

    // First, sort stats by event_received_time to get the latest records first
    const sortedStats = [...stats].sort((a, b) => 
      new Date(b.event_received_time).getTime() - new Date(a.event_received_time).getTime()
    );
    
    // Group by client-site, using only the LATEST record for each combination
    sortedStats.forEach(stat => {
      const key = `${stat.client_id}-${stat.site_id}`;
      
      // Normalize field name: API returns 'additional' but code expects 'additional_notes'
      const additionalData = (stat as any).additional || stat.additional_notes || {};
      
      // Only process if we haven't seen this client-site combination yet
      // Since we sorted by newest first, the first one we see is the latest
      if (!groups[key]) {
        groups[key] = {
          id: key,
          client_id: stat.client_id,
          site_id: stat.site_id,
          start_time: stat.start_time ?? '',
          end_time: stat.end_time ?? '',
          total_images_processed: stat.stats.images_processed_current || stat.stats.images_processed || 0,
          total_images_synced: stat.stats.images_synced_current || 0,
          total_images_pending: stat.stats.images_sync_pending_current || stat.stats.image_sync_pending || 0,
          patients_synced: stat.stats.patients_synced_current || stat.stats.images_synced_current || 0,
          patients_not_synced: stat.stats.patients_not_synced || 0,
          patients_total: stat.stats.patients_total || 0,
          record_count: 1,
          latest_event: stat.event_received_time,
          sync_status: { color: '', bgColor: '', text: '' },
          raw_records: [stat],
          additional_data: {
            priorities: additionalData?.priority ? [additionalData.priority] : [],
            tags: additionalData?.tags || [],
            processing_nodes: additionalData?.metadata?.processing_node ? [additionalData.metadata.processing_node] : [],
            environments: additionalData?.metadata?.environment ? [additionalData.metadata.environment] : [],
            error_count: additionalData?.error_details ? 1 : 0,
            total_batches: 1,
          }
        };
      } else {
        // If group already exists, just add to record count and raw_records for modal viewing
        // But keep the values from the FIRST (newest) record we saw
        const group = groups[key];
        group.record_count += 1;
        group.raw_records.push(stat);
        
        // Update time range to show the full range
        if (new Date(stat.start_time ?? '') < new Date(group.start_time)) {
          group.start_time = stat.start_time ?? '';
        }
        if (new Date(stat.end_time ?? '') > new Date(group.end_time)) {
          group.end_time = stat.end_time ?? '';
        }
        if (new Date(stat.event_received_time) > new Date(group.latest_event)) {
          group.latest_event = stat.event_received_time;
        }

        // Collect additional data from all records
        if (additionalData?.priority && !group.additional_data.priorities.includes(additionalData.priority)) {
          group.additional_data.priorities.push(additionalData.priority);
        }
        if (additionalData?.tags) {
          additionalData.tags.forEach((tag: string) => {
            if (!group.additional_data.tags.includes(tag)) {
              group.additional_data.tags.push(tag);
            }
          });
        }
        if (additionalData?.metadata?.processing_node && !group.additional_data.processing_nodes.includes(additionalData.metadata.processing_node)) {
          group.additional_data.processing_nodes.push(additionalData.metadata.processing_node);
        }
        if (additionalData?.metadata?.environment && !group.additional_data.environments.includes(additionalData.metadata.environment)) {
          group.additional_data.environments.push(additionalData.metadata.environment);
        }
        if (additionalData?.error_details) {
          group.additional_data.error_count += 1;
        }
        group.additional_data.total_batches += 1;
      }
    });

    // Calculate sync percentage for each group
    Object.values(groups).forEach(group => {
      const synced = group.patients_synced;
      const total = group.patients_total > 0 ? group.patients_total : (synced + group.patients_not_synced);

      if (total === 0) {
        group.sync_status = { color: "rgba(255,255,255,0.4)", bgColor: "rgba(255,255,255,0.06)", text: "No Data" };
      } else {
        const pct = Math.round((synced / total) * 100);
        let color: string, bgColor: string;
        if (pct >= 90) {
          color = "#22c55e"; bgColor = "rgba(34, 197, 94, 0.1)";
        } else if (pct >= 50) {
          color = "#eab308"; bgColor = "rgba(234, 179, 8, 0.1)";
        } else {
          color = "#ef4444"; bgColor = "rgba(239, 68, 68, 0.1)";
        }
        group.sync_status = { color, bgColor, text: `${pct}% Synced` };
      }
    });

    return Object.values(groups).sort((a, b) => {
      if (a.client_id !== b.client_id) return a.client_id.localeCompare(b.client_id);
      return a.site_id.localeCompare(b.site_id);
    });
  }, [stats]);

  // site_name → project_name lookup from machines list
  const siteToProject = React.useMemo(() => {
    const map = new Map<string, string>();
    machines.forEach((m: any) => {
      if (m.site_name && m.project_name) {
        map.set(m.site_name, m.project_name);
      }
    });
    return map;
  }, [machines]);

  // Group groupedStats by project name
  const projectGroups = React.useMemo(() => {
    const groupMap = new Map<string | null, GroupedStats[]>();
    groupedStats.forEach(stat => {
      const projectName = siteToProject.get(stat.site_id) ?? null;
      if (!groupMap.has(projectName)) groupMap.set(projectName, []);
      groupMap.get(projectName)!.push(stat);
    });
    // Named projects first (alphabetically), ungrouped sites last
    const result: { name: string | null; sites: GroupedStats[] }[] = [];
    const sortedKeys = Array.from(groupMap.keys()).sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    });
    sortedKeys.forEach(key => result.push({ name: key, sites: groupMap.get(key)! }));
    return result;
  }, [groupedStats, siteToProject]);

  // Auto-expand all projects on first load
  React.useEffect(() => {
    const names = projectGroups.filter(g => g.name !== null).map(g => g.name as string);
    if (names.length > 0) {
      setExpandedProjects(new Set(names));
    }
  }, [projectGroups]);

  const toggleProject = (name: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const handleRowClick = (groupedStat: GroupedStats) => {
    setSelectedRow(groupedStat.id);
    // Show the most recent record in the modal
    const latestRecord = groupedStat.raw_records.reduce((latest, current) => 
      new Date(current.event_received_time) > new Date(latest.event_received_time) ? current : latest
    );
    setModalData(latestRecord);
    setIsModalOpen(true);
  };

  const handleAdditionalClick = (groupedStat: GroupedStats, event: React.MouseEvent) => {
    event.stopPropagation();
    // Show the most recent record in the modal
    const latestRecord = groupedStat.raw_records.reduce((latest, current) => 
      new Date(current.event_received_time) > new Date(latest.event_received_time) ? current : latest
    );
    setModalData(latestRecord);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  const handleDockerMetricsClick = (dockerMetrics: any, siteId: string, clientId: string) => {
    setDockerMetricsData(dockerMetrics);
    setDockerMetricsSiteId(siteId);
    setDockerMetricsClientId(clientId);
    setIsDockerMetricsModalOpen(true);
  };

  const closeDockerMetricsModal = () => {
    setIsDockerMetricsModalOpen(false);
    setDockerMetricsData(null);
    setDockerMetricsSiteId('');
    setDockerMetricsClientId('');
  };

  const handleVitalsMetricsClick = (vitalsMetrics: any, siteId: string, clientId: string) => {
    setVitalsMetricsData(vitalsMetrics);
    setVitalsMetricsSiteId(siteId);
    setVitalsMetricsClientId(clientId);
    setIsVitalsMetricsModalOpen(true);
  };

  const closeVitalsMetricsModal = () => {
    setIsVitalsMetricsModalOpen(false);
    setVitalsMetricsData(null);
    setVitalsMetricsSiteId('');
    setVitalsMetricsClientId('');
  };

  const handleSystemMetricsClick = (systemMetrics: any, siteId: string, clientId: string) => {
    setSystemMetricsData(systemMetrics);
    setSystemMetricsSiteId(siteId);
    setSystemMetricsClientId(clientId);
    setIsSystemMetricsModalOpen(true);
  };

  const closeSystemMetricsModal = () => {
    setIsSystemMetricsModalOpen(false);
    setSystemMetricsData(null);
    setSystemMetricsSiteId('');
    setSystemMetricsClientId('');
  };

  const handleQureappMetricsClick = (qStats: any, siteId: string, clientId: string) => {
    setQureappMetricsData(qStats);
    setQureappMetricsSiteId(siteId);
    setQureappMetricsClientId(clientId);
    setIsQureappMetricsModalOpen(true);
  };

  const closeQureappMetricsModal = () => {
    setIsQureappMetricsModalOpen(false);
    setQureappMetricsData(null);
    setQureappMetricsSiteId('');
    setQureappMetricsClientId('');
  };

  const handleNvidiaMetricsClick = (nvidiaMetrics: any, siteId: string, clientId: string) => {
    setNvidiaMetricsData(nvidiaMetrics);
    setNvidiaMetricsSiteId(siteId);
    setNvidiaMetricsClientId(clientId);
    setIsNvidiaMetricsModalOpen(true);
  };

  const closeNvidiaMetricsModal = () => {
    setIsNvidiaMetricsModalOpen(false);
    setNvidiaMetricsData(null);
    setNvidiaMetricsSiteId('');
    setNvidiaMetricsClientId('');
  };

  const handleLocationClick = (location: any, siteId: string, clientId: string) => {
    setLocationData(location);
    setLocationSiteId(siteId);
    setLocationClientId(clientId);
    setIsLocationModalOpen(true);
  };

  const closeLocationModal = () => {
    setIsLocationModalOpen(false);
    setLocationData(null);
    setLocationSiteId('');
    setLocationClientId('');
  };

  // Pagination logic
  const totalPages = Math.ceil(groupedStats.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedRow(null); // Clear selected row when changing page
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };

  if (error) {
    return (
      <Card className="border-0" style={{ background: "#0f0f0f" }}>
        <CardContent className="p-6">
          <div className="text-center" style={{ color: "#ef4444" }}>
            Error: {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        className="border-0 rounded-2xl"
        style={{
          backgroundColor: "#0f1717",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <CardHeader className="px-6 py-5 border-b" style={{ borderColor: "rgba(255, 255, 255, 0.06)" }}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
              <Database className="h-4 w-4" style={{ color: "#00A9A7" }} />
              Event Activity
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                onRefresh();
              }}
              disabled={loading}
              className="border-0 h-8 px-3 text-sm font-medium transition-all"
              style={{
                backgroundColor: "rgba(0, 169, 167, 0.1)",
                color: "#00A9A7",
                border: "1px solid rgba(0, 169, 167, 0.25)",
              }}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.06)", backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Client
                  </th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Site
                  </th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Start Time
                  </th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    End Time
                  </th>
                  <th className="text-right px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Images
                  </th>
                  <th className="text-center px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Sync %
                  </th>
                  <th className="text-center px-6 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {projectGroups.map(group => {
                  const isExpanded = group.name === null || expandedProjects.has(group.name);

                  const siteRows = (sites: GroupedStats[], indented: boolean) =>
                    sites.map((groupedStat, index) => (
                      <tr
                        key={groupedStat.id}
                        onClick={() => handleRowClick(groupedStat)}
                        className="transition-all duration-150 cursor-pointer"
                        style={{
                          borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
                          backgroundColor: selectedRow === groupedStat.id
                            ? "rgba(0, 169, 167, 0.08)"
                            : index % 2 === 0 ? "rgba(255, 255, 255, 0.01)" : "transparent",
                          borderLeft: selectedRow === groupedStat.id ? "3px solid #00A9A7" : "3px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (selectedRow !== groupedStat.id)
                            (e.currentTarget as HTMLTableRowElement).style.backgroundColor = "rgba(0, 169, 167, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          if (selectedRow !== groupedStat.id)
                            (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                              index % 2 === 0 ? "rgba(255, 255, 255, 0.01)" : "transparent";
                        }}
                      >
                        <td className="px-6 py-5 text-left">
                          <div className="text-sm font-medium" style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                            {groupedStat.client_id}
                          </div>
                        </td>
                        <td className={`py-5 text-left ${indented ? "pl-10 pr-6" : "px-6"}`}>
                          <div className="text-sm font-semibold" style={{ color: "#5eead4" }}>
                            {groupedStat.site_id}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-left">
                          <div className="text-sm" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                            {new Date(groupedStat.start_time).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-left">
                          <div className="text-sm" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                            {new Date(groupedStat.end_time).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="text-sm font-semibold tabular-nums" style={{ color: "rgba(255, 255, 255, 0.85)" }}>
                            {groupedStat.total_images_processed.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className="flex justify-center">
                            <span
                              className="text-xs font-semibold px-3 py-1 rounded-full"
                              style={{
                                backgroundColor: groupedStat.sync_status.bgColor,
                                color: groupedStat.sync_status.color,
                                border: `1px solid ${groupedStat.sync_status.color}40`,
                              }}
                            >
                              {groupedStat.sync_status.text}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className="flex justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleAdditionalClick(groupedStat, e)}
                              className="border-0 h-8 px-3 text-xs font-medium transition-all"
                              style={{
                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                color: "rgba(255, 255, 255, 0.6)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              View ({groupedStat.record_count})
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ));

                  if (group.name === null) {
                    // Ungrouped sites — render flat rows as before
                    return <React.Fragment key="ungrouped">{siteRows(group.sites, false)}</React.Fragment>;
                  }

                  return (
                    <React.Fragment key={`project-${group.name}`}>
                      {/* Project header row */}
                      <tr
                        onClick={() => toggleProject(group.name!)}
                        className="cursor-pointer select-none"
                        style={{
                          backgroundColor: "rgba(0, 169, 167, 0.07)",
                          borderTop: "1px solid rgba(0, 169, 167, 0.2)",
                          borderBottom: isExpanded ? "none" : "1px solid rgba(0, 169, 167, 0.2)",
                        }}
                      >
                        <td colSpan={7} className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <ChevronDown
                              className="h-4 w-4 flex-shrink-0 transition-transform duration-200"
                              style={{
                                color: "#00A9A7",
                                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                              }}
                            />
                            <span className="text-sm font-semibold" style={{ color: "#00A9A7" }}>
                              {group.name}
                            </span>
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{
                                backgroundColor: "rgba(0, 169, 167, 0.15)",
                                color: "#00A9A7",
                                border: "1px solid rgba(0, 169, 167, 0.3)",
                              }}
                            >
                              {group.sites.length} {group.sites.length === 1 ? "site" : "sites"}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {/* Site rows (when expanded) */}
                      {isExpanded && siteRows(group.sites, true)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {loading && (
              <div className="p-8 text-center text-sm" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                Loading events...
              </div>
            )}

            {!loading && groupedStats.length === 0 && (
              <div className="p-10 text-center rounded-xl mx-4 my-4" style={{ backgroundColor: "rgba(255, 255, 255, 0.02)", border: "1px dashed rgba(255, 255, 255, 0.1)" }}>
                <div className="text-sm font-medium" style={{ color: "rgba(255, 255, 255, 0.5)" }}>No events available.</div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && groupedStats.length > 0 && projectGroups.every(g => g.name === null) && (
            <div
              className="px-6 py-4 flex flex-wrap items-center justify-between gap-4"
              style={{ borderTop: "1px solid rgba(255, 255, 255, 0.06)" }}
            >
              {/* Left: count */}
              <div className="text-xs" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                Showing {groupedStats.length === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, groupedStats.length)} of {groupedStats.length} sites
              </div>

              {/* Right: page size selector + navigation */}
              <div className="flex items-center gap-3">

                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: "rgba(255, 255, 255, 0.4)" }}>Rows per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="rounded-lg px-2 py-1 text-xs"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255, 255, 255, 0.7)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      outline: "none",
                    }}
                  >
                    {[20, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                {/* Page navigation */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-0 h-8 px-3 text-xs"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        color: currentPage === 1 ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.6)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                      }}
                    >
                      ← Prev
                    </Button>

                    {getPageNumbers().map((page) => (
                      <Button
                        key={page}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="border-0 h-8 w-8 p-0 text-xs"
                        style={{
                          backgroundColor: currentPage === page ? "#00A9A7" : "rgba(255, 255, 255, 0.05)",
                          color: currentPage === page ? "#ffffff" : "rgba(255, 255, 255, 0.6)",
                          border: currentPage === page ? "1px solid #00A9A7" : "1px solid rgba(255, 255, 255, 0.08)",
                        }}
                      >
                        {page}
                      </Button>
                    ))}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-0 h-8 px-3 text-xs"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        color: currentPage === totalPages ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.6)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                      }}
                    >
                      Next →
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AdditionalInfoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        data={modalData}
        onDockerMetricsClick={handleDockerMetricsClick}
        onSystemMetricsClick={handleSystemMetricsClick}
        onQureappMetricsClick={handleQureappMetricsClick}
        onNvidiaMetricsClick={handleNvidiaMetricsClick}
        onLocationClick={handleLocationClick}
        onVitalsMetricsClick={handleVitalsMetricsClick}
      />

      <DockerMetricsModal
        isOpen={isDockerMetricsModalOpen}
        onClose={closeDockerMetricsModal}
        dockerMetrics={dockerMetricsData}
        siteId={dockerMetricsSiteId}
        clientId={dockerMetricsClientId}
      />

      <VitalsMetricsModal
        isOpen={isVitalsMetricsModalOpen}
        onClose={closeVitalsMetricsModal}
        vitalsMetrics={vitalsMetricsData}
        siteId={vitalsMetricsSiteId}
        clientId={vitalsMetricsClientId}
      />

      <SystemMetricsModal
        isOpen={isSystemMetricsModalOpen}
        onClose={closeSystemMetricsModal}
        systemMetrics={systemMetricsData}
        siteId={systemMetricsSiteId}
        clientId={systemMetricsClientId}
      />

      <QureappMetricsModal
        isOpen={isQureappMetricsModalOpen}
        onClose={closeQureappMetricsModal}
        stats={qureappMetricsData}
        siteId={qureappMetricsSiteId}
        clientId={qureappMetricsClientId}
      />

      <NvidiaMetricsModal
        isOpen={isNvidiaMetricsModalOpen}
        onClose={closeNvidiaMetricsModal}
        nvidiaMetrics={nvidiaMetricsData}
        siteId={nvidiaMetricsSiteId}
        clientId={nvidiaMetricsClientId}
      />

      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={closeLocationModal}
        location={locationData}
        siteId={locationSiteId}
        clientId={locationClientId}
      />
    </>
  );
};
